import { BaseClass } from './__base'
import { MediaItemFile } from './__MediaLibrary'
import { Strings } from '../../utils/strings'
import { CMSFile } from './File'
import { Objects } from '../../utils/objects'
import { Numbers } from '../../utils/numbers'
import { CancelTokenSource } from 'axios'
import Client from '../client'
import { getRecoil, setRecoil } from '../../state/recoilNexus'
import { InstanceOf } from './index'
import { Types } from '../types'
import { ModelInfo } from './__ModelInfo'
import {
	cacheBuster,
	productDataSelector,
	references
} from '../../state/state'
import {
	hasUploadedFileToggle
} from '../../components/Bulk/state'
import { ContentType } from './ContentType'
import { Lists } from '../../utils/lists'
import { Dates } from '../../utils/dates'
import {
	generateTree,
	Node,
	NodesMap,
	NodesMapType,
	selectUp
} from '../../components/Form/renderers/components/ProductDataSelect/utils'
import { Component } from './Component'
import { ChildAssessment } from './ChildAssessment'
import { CTUnit } from './CTUnit'
import { DisplayMode } from './__CMSObject'
import { debounce } from 'lodash'
import { fi } from '../../utils/helpers'

export type UploadInfo = {
	file: File
	progress: number
	cancel?: CancelTokenSource
}

export class BulkFile extends BaseClass {
	public static files: BulkFile[] = []
	private static idGen: number = -1
	public static models: { [key: string]: ModelInfo } = {}

	// Db row id
	public bulkid: number
	// CMS file UUID
	public fileid: string
	public filename: string
	// Bulk File error (processing or uploading error)
	public error: string
	// Item data
	public data: any

	public errors: any = {}

	// CMS file info
	public fileInfo?: CMSFile
	public item?: MediaItemFile
	public uploadInfo?: UploadInfo

	public selected: boolean = false
	private persistData: (...args: any[]) => any
	private saveInFlight: Promise<void> | null = null

	constructor(tmp: any = {}) {
		const item = JSON.parse(JSON.stringify(tmp))
		if (!item.data) {
			item.data = {}
		}
		if (!item.data.__type) {
			item.data.__type = Types.DOCUMENT
		}

		if (item.data.__tag) {
			if (typeof item.data.__tag === 'string') {
				item.data.__tag = item.data.__tag
					.split(',')
					.map((s: string) => s.trim())
			} else if (typeof item.data.__tag === 'undefined') {
				item.data.__tag = []
			}
		}

		super(item)

		this.persistData = debounce(async (files: any[]) => {
			return Client.updateBulkFiles(files)
		}, 500)

		this.bulkid = Numbers.default(item.bulkid, --BulkFile.idGen)
		this.fileid = Strings.default(item.fileid)
		this.filename = Strings.default(item.filename)
		this.error = Strings.default(item.error)
		this.data = Objects.default(item.data)

		if (this.data.series) {
			this.data.series = this.data.series.toUpperCase()
		}

		if (item.file) {
			this.fileInfo = new CMSFile(item.file)
			setRecoil(references(item.file.fileuid), this.fileInfo)
		}

		if (this.data.__type) {
			this.item = InstanceOf(this.data)
			this.data.productData = this.item?.productData

			if (BulkFile.models[this.data.__type]) {
				this.errors = BulkFile.models[this.data.__type].validate(this.data)
			}
		}
	}

	public uploadFile(file: File): void {
		this.filename = file.name
		this.uploadInfo = {
			file: file,
			progress: 0
		}

		const [upload, cancel] = Client.upload(file, (progress) => {
			this.uploadInfo!.progress = (progress.percent * 175) / 100
			this.invalidateCache()
		})

		this.uploadInfo!.cancel = cancel
		upload
			.then((res) => {
				this.fileid = res.fileuid
				this.data.file = res.fileuid
				this.fileInfo = res
				if (res.isAudio()) {
					this.data.__type = Types.AUDIO
				} else {
					this.data.__type = Types.DOCUMENT
				}
				this.item = InstanceOf(this.data)

				setRecoil(references(res.fileuid), res)
				this.setData({ ...this.data }, true)
			})
			.catch((err) => {
				this.error = err.message
			})
			.finally(() => {
				this.uploadInfo = undefined
				this.invalidateCache()
				setRecoil(hasUploadedFileToggle, (prev) => !prev)
			})

		this.invalidateCache()
	}

	public setData(data: any = {}, persisit: boolean = false) {
		this.data = { ...this.data, ...data }

		if (BulkFile.models[this.data.__type]) {
			this.errors = BulkFile.models[this.data.__type].validate(this.data)
		}

		if (persisit) {
			this.save().catch((err) => {
				this.error = err.message
			})
		}
		this.invalidateCache()
	}

	public async save(): Promise<void> {
		if (this.saveInFlight) {
			await this.saveInFlight
			// After waiting, bulkid may have been assigned; re-run save to persist latest data
			return this.save()
		}

		const doSave = async () => {
			if (this.bulkid < 1) {
				const obj: any = {
					fileid: this.fileid,
					filename: this.filename,
					data: this.data
				}
				if (!obj.fileid) {
					delete obj.fileid
				}
				const tmp = await Client.addBulkItem(obj as BulkFile)
				this.bulkid = tmp.bulkid
				this.error = Strings.default(tmp.error)
			} else {
				const obj: any = {
					bulkid: this.bulkid,
					fileid: this.fileid,
					filename: this.filename,
					data: this.data
				}
				if (!obj.fileid) {
					delete obj.fileid
				}
				const tmp = await this.persistData([obj as BulkFile])
				if (tmp && tmp.length === 1) {
					this.error = Strings.default(tmp[0].error)
				}
			}
			this.invalidateCache()
		}

		this.saveInFlight = doSave().finally(() => {
			this.saveInFlight = null
		})
		return this.saveInFlight
	}

	public invalidateCache(): void {
		setRecoil(cacheBuster(`bulk-file-${this.filename}`), (val) => val + 1)
	}



	get title(): string {
		return Strings.default(Objects.default(this.data).title)
	}

	get description(): string {
		return Strings.default(Objects.default(this.data).description)
	}

	get __tag(): string {
		return Lists.default(Objects.default(this.data).__tag).join(', ')
	}

	get productData(): string {
		if (
			this.data.all_assessments ||
			this.data.productData === 'all_assessments'
		) {
			return 'All products selected'
		}

		let tmp: any
		if (!NodesMap[this.filename]) {
			const tree = getRecoil(productDataSelector)
			const nodesTree: NodesMapType = {}
			generateTree(tree, false, nodesTree)
			NodesMap[this.filename] = nodesTree
		}

		tmp = NodesMap[this.filename]

		for (let k in tmp) {
			const obj = tmp[k]
			if (
				obj.object instanceof Component &&
				Lists.default(this.data.component).includes(obj.id)
			) {
				obj.selected = true
				selectUp(obj)
			}
			if (
				obj.object instanceof ChildAssessment &&
				Lists.default(this.data.child_assessment).includes(obj.id)
			) {
				obj.selected = true
				selectUp(obj)
			}
			if (
				obj.object instanceof CTUnit &&
				Lists.default(this.data.ct_unit).includes(obj.id)
			) {
				obj.selected = true
				selectUp(obj)
			}
		}

		const list: any[] = []
		const added: any = {}
		const walk = (node: Node) => {
			if (node.selected) {
				if (!added[node.id]) {
					list.push(node.object.displayLabel(DisplayMode.SHORT))
					added[node.id] = true
				}
			} else {
				for (let k in node.children) {
					walk(node.children[k])
				}
			}
		}
		tmp[''].children.forEach(walk)
		return list.join(', ')
	}

	get public_from(): string {
		if (Objects.default(this.data).public_from) {
			return Dates.local(Objects.default(this.data).public_from)
		}
		return ''
	}

	get paper_number(): string {
		return Strings.default(Objects.default(this.data).paper_number)
	}

	get series(): string {
		return Strings.capitalize(
			Strings.default(Objects.default(this.data).series)
		)
	}

	get exam_year(): string {
		const years = this.data.exam_year
		if (Array.isArray(years)) {
			let result = ''
			let i = 0

			while (i < years.length) {
				let start = years[i]
				let end = start

				while (years[i + 1] === end + 1) {
					end = years[i + 1]
					i++
				}

				result += start === end ? `${start}, ` : `${start}-${end}, `
				i++
			}
			return result.slice(0, -2)
		} else {
			return Strings.default(Objects.default(this.data).exam_year)
		}
	}

	get content_type(): string {
		if (Objects.default(this.data).content_type) {
			const ct = getRecoil(
				references(this.data.content_type)
			) as any as ContentType
			if (ct) {
				return ct.name
			}
		}
		return ''
	}

	get uploadProgress(): number {
		if (this.uploadInfo) {
			return this.uploadInfo.progress
		}
		return 0
	}

	get formattedErrors(): string[] {
		const res: string[] = []
		for (let k in this.errors) {
			const e = this.errors[k]
			switch (k) {
				case 'file':
					res.push(`File - ${e}`)
					break
				case 'title':
					res.push(`Title - ${e}`)
					break
				case 'productData':
					res.push(`Product mapping - ${e}`)
					break
				case 'content_type':
					res.push(`Content type - ${e}`)
					break
				case 'description':
					res.push(`Title - ${e}`)
					break
				case 'exam_year':
					res.push(`Exam year - ${e}`)
					break
				case 'series':
					res.push(`Series - ${e}`)
					break
				case '__tag':
					res.push(`Tags - ${e}`)
					break
				case 'public_from':
					res.push(`Public date - ${e}`)
					break
			}
		}
		return res
	}

	get errorMessage(): string {
		if (this.error) {
			return this.error
		}
		const errors = Object.keys(this.errors).length
		if (errors > 0) {
			return `${errors} Error${fi(errors > 1, 's', '')}`
		}
		return ''
	}

	get errorMessages(): string {
		if (this.error) {
			return this.error
		}
		return this.formattedErrors.join('\n')
	}

	public cancelUpload(): void {
		if (this.uploadInfo && this.uploadInfo.cancel) {
			this.uploadInfo.cancel.cancel()
			this.error = 'Canceled'
		}
	}
}
